import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Clock, Fingerprint, FileText, Download, Loader, AlertCircle, Calendar, Phone } from "lucide-react";

const ClientStatusPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchStatus();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchStatus = async () => {
    try {
      const resp = await axios.get(`/public/intake/${token}/status`);
      setStatus(resp.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to load status.");
    } finally {
      setLoading(false);
    }
  };

  const downloadCard = async (cardType) => {
    setDownloading(cardType);
    try {
      const resp = await axios.get(`/public/intake/${token}/download-card/${cardType}`);
      const { pdf_base64, filename } = resp.data;
      const byteChars = atob(pdf_base64);
      const byteNums = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNums)], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.detail || "Card not yet available.");
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader className="w-10 h-10 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
          <AlertCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Link Invalid</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  const steps = [
    {
      key: "intake_submitted",
      label: "Information Submitted",
      icon: FileText,
      done: status?.status_steps?.intake_submitted,
    },
    {
      key: "appointment_booked",
      label: "Appointment Scheduled",
      icon: Calendar,
      done: status?.status_steps?.appointment_booked,
    },
    {
      key: "fingerprints_captured",
      label: "Fingerprints Captured",
      icon: Fingerprint,
      done: status?.status_steps?.fingerprints_captured,
      detail: status?.fingerprint_count > 0 ? `${status.fingerprint_count}/10 fingers` : null,
    },
    {
      key: "complete",
      label: "Card Ready for Download",
      icon: Download,
      done: status?.card_available,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-900 relative" data-testid="client-status-page">
      <div className="fixed inset-0 z-0 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" />
      <div className="relative z-10 py-8 px-4">
        <div className="max-w-2xl mx-auto">

          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-cyan-500/20 rounded-2xl mb-3">
              <Fingerprint className="w-7 h-7 text-cyan-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-1" data-testid="status-title">
              {status?.client_name ? `${status.client_name}'s Status` : "Your Fingerprinting Status"}
            </h1>
            {status?.organization && (
              <p className="text-gray-400 text-sm">Provider: {status.organization}</p>
            )}
          </div>

          {/* Appointment Info */}
          {status?.appointment && (
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 mb-8 flex items-center gap-3" data-testid="appointment-info">
              <Calendar className="w-5 h-5 text-cyan-400 flex-shrink-0" />
              <div>
                <p className="text-white font-medium">Appointment: {status.appointment.date} at {status.appointment.time}</p>
                <p className="text-gray-400 text-sm capitalize">Status: {status.appointment.status}</p>
              </div>
            </div>
          )}

          {/* Progress Steps */}
          <div className="bg-slate-800/80 border border-white/10 rounded-2xl p-6 mb-8" data-testid="progress-steps">
            <div className="space-y-0">
              {steps.map((step, i) => {
                const Icon = step.icon;
                const isActive = !step.done && (i === 0 || steps[i - 1].done);
                return (
                  <div key={step.key} className="relative">
                    {/* Connector line */}
                    {i > 0 && (
                      <div className={`absolute left-[19px] -top-3 w-0.5 h-6 ${steps[i - 1].done ? "bg-green-500" : "bg-gray-700"}`} />
                    )}
                    <div className={`flex items-center gap-4 py-3 px-3 rounded-xl transition-colors ${isActive ? "bg-cyan-500/10" : ""}`}>
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        step.done ? "bg-green-500/20 text-green-400" : isActive ? "bg-cyan-500/20 text-cyan-400 animate-pulse" : "bg-gray-700/50 text-gray-500"
                      }`}>
                        {step.done ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium ${step.done ? "text-green-400" : isActive ? "text-white" : "text-gray-500"}`}>
                          {step.label}
                        </p>
                        {step.detail && <p className="text-xs text-gray-400">{step.detail}</p>}
                        {isActive && <p className="text-xs text-cyan-300 mt-0.5">In progress...</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Card Download Section */}
          {status?.card_available && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6 mb-8" data-testid="card-download-section">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Download className="w-5 h-5 text-green-400" />
                Download Your Fingerprint Card
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <button
                  data-testid="download-fd258-btn"
                  onClick={() => downloadCard("fd258")}
                  disabled={downloading === "fd258"}
                  className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-xl p-4 text-center transition-colors"
                >
                  {downloading === "fd258" ? (
                    <Loader className="w-5 h-5 animate-spin mx-auto mb-1" />
                  ) : (
                    <FileText className="w-6 h-6 mx-auto mb-1" />
                  )}
                  <p className="font-semibold text-sm">FD-258</p>
                  <p className="text-xs text-green-200">Applicant Card</p>
                </button>
                <button
                  data-testid="download-fd1164-btn"
                  onClick={() => downloadCard("fd1164")}
                  disabled={downloading === "fd1164"}
                  className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-xl p-4 text-center transition-colors"
                >
                  {downloading === "fd1164" ? (
                    <Loader className="w-5 h-5 animate-spin mx-auto mb-1" />
                  ) : (
                    <FileText className="w-6 h-6 mx-auto mb-1" />
                  )}
                  <p className="font-semibold text-sm">FD-1164</p>
                  <p className="text-xs text-green-200">Criminal Card</p>
                </button>
              </div>
            </div>
          )}

          {/* Help Section */}
          <div className="bg-slate-800/50 border border-white/5 rounded-xl p-4 text-center">
            <Phone className="w-5 h-5 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">
              Questions? Contact your fingerprinting provider directly.
            </p>
            <p className="text-gray-500 text-xs mt-1">This page refreshes automatically.</p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ClientStatusPage;
